<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, watch } from "vue";
import { RouterLink, RouterView, useRoute, useRouter } from "vue-router";
import { useAuthStore } from "@/stores/auth";
import ToastContainer from "@/components/ToastContainer.vue";
import coordinatorLogo from "@/assets/new logo.png";

const auth = useAuthStore();
const router = useRouter();
const route = useRoute();

// Scroll main content area back to the top on every navigation.
// Without this, the scroll position from the previous page is preserved
// inside .main-content (the scrollable flex child), making the new page
// appear blank when the content is shorter than the previous scroll offset.
const mainContentRef = ref<HTMLElement | null>(null);
watch(
  () => route.fullPath,
  () => mainContentRef.value?.scrollTo({ top: 0 }),
  { flush: "post" },
);

const showShell = computed(() => auth.isAuthenticated);

const isJobsSection = computed(() =>
  ["job-detail", "job-new", "job-import", "batch-detail"].includes(
    route.name as string,
  ),
);

const userInitial = computed(() => {
  const s = auth.user?.name || auth.user?.email || "?";
  return s.charAt(0).toUpperCase();
});

const shortName = computed(() => {
  const n = auth.user?.name;
  if (n) {
    const parts = n.split(" ");
    return parts[0] + (parts[1] ? " " + parts[1][0] + "." : "");
  }
  return (auth.user?.email ?? "").split("@")[0];
});
const displayUserName = computed(() => auth.user?.name || shortName.value || "User");
const userRoleLabel = computed(() => {
  const roles = auth.user?.roles ?? [];
  if (roles.includes("admin")) return "Administrator";
  if (roles.includes("coordinator")) return "Coordinator";
  if (roles.includes("technician")) return "Technician";
  return "User";
});
const accountMenuOpen = ref(false);
const accountMenuRef = ref<HTMLElement | null>(null);

function closeAccountMenu() {
  accountMenuOpen.value = false;
}

function toggleAccountMenu() {
  accountMenuOpen.value = !accountMenuOpen.value;
}

function handleDocumentPointerDown(event: MouseEvent) {
  if (!accountMenuOpen.value) return;
  const target = event.target as Node | null;
  if (!target) return;
  if (accountMenuRef.value?.contains(target)) return;
  closeAccountMenu();
}

function handleDocumentKeydown(event: KeyboardEvent) {
  if (event.key === "Escape") {
    closeAccountMenu();
  }
}

async function signOut() {
  closeAccountMenu();
  await auth.signOut();
  router.replace({ name: "login" });
}

onMounted(() => {
  document.addEventListener("mousedown", handleDocumentPointerDown);
  document.addEventListener("keydown", handleDocumentKeydown);
});

onBeforeUnmount(() => {
  document.removeEventListener("mousedown", handleDocumentPointerDown);
  document.removeEventListener("keydown", handleDocumentKeydown);
});
</script>

<template>
  <div class="app">

    <!-- Authenticated shell: sidebar + content -->
    <div v-if="showShell" class="app-body">
      <aside class="sidebar">
        <div class="sidebar-brand">
          <RouterLink to="/dashboard" class="brand-logo-link" aria-label="Go to dashboard">
            <span class="brand-icon-wrap">
              <img :src="coordinatorLogo" alt="Coordinator logo" class="brand-c-image" />
            </span>
          </RouterLink>
        </div>
        <nav class="sidebar-nav">
          <RouterLink
            to="/dashboard" active-class=""
            class="nav-item" :class="{ 'nav-active': route.name === 'dashboard' || isJobsSection }"
          >
            <span class="nav-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75">
                <rect x="3" y="3" width="7" height="7" rx="1.5"/>
                <rect x="14" y="3" width="7" height="7" rx="1.5"/>
                <rect x="3" y="14" width="7" height="7" rx="1.5"/>
                <rect x="14" y="14" width="7" height="7" rx="1.5"/>
              </svg>
            </span>
            <span>Dashboard</span>
          </RouterLink>

          <RouterLink
            to="/scheduler" active-class=""
            class="nav-item"
            :class="{ 'nav-active': route.name === 'scheduler' }"
          >
            <span class="nav-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75">
                <rect x="3" y="4" width="18" height="18" rx="2"/>
                <path d="M3 9h18M8 2v4m8-4v4"/>
              </svg>
            </span>
            <span>Scheduler</span>
          </RouterLink>

          <RouterLink
            v-if="auth.isCoordinator"
            to="/teams" active-class=""
            class="nav-item"
            :class="{ 'nav-active': route.name === 'teams' || route.name === 'team-detail' }"
          >
            <span class="nav-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75">
                <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
                <circle cx="9" cy="7" r="4"/>
                <path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75"/>
              </svg>
            </span>
            <span>Teams</span>
          </RouterLink>



          <RouterLink
            v-if="auth.isCoordinator"
            to="/reports" active-class=""
            class="nav-item"
            :class="{ 'nav-active': route.name === 'reports' }"
          >
            <span class="nav-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75">
                <path d="M9 19v-6a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v6a2 2 0 0 0 2 2h2a2 2 0 0 0 2-2zm0 0V9a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v10m-6 0a2 2 0 0 0 2 2h2a2 2 0 0 0 2-2m0 0V5a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-2a2 2 0 0 1-2-2z"/>
              </svg>
            </span>
            <span>Reports</span>
          </RouterLink>

          <span class="nav-item nav-disabled">
            <span class="nav-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75">
                <path d="M4 4v5h.582m15.356 2A8.001 8.001 0 0 0 4.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 0 1-15.357-2m15.357 2H15"/>
              </svg>
            </span>
            <span>Revisit Tasks</span>
          </span>

          <span class="nav-item nav-disabled">
            <span class="nav-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75">
                <circle cx="12" cy="12" r="3"/>
                <path d="M19.07 4.93a10 10 0 0 1 0 14.14M4.93 4.93a10 10 0 0 0 0 14.14"/>
              </svg>
            </span>
            <span>Settings</span>
            <span class="soon-badge">Soon</span>
          </span>
        </nav>

        <div ref="accountMenuRef" class="sidebar-account-menu">
          <button
            type="button"
            class="sidebar-account-summary"
            :aria-expanded="accountMenuOpen ? 'true' : 'false'"
            aria-haspopup="menu"
            @click="toggleAccountMenu"
          >
            <div class="user-avatar">{{ userInitial }}</div>
            <div class="sidebar-account-meta">
              <span class="sidebar-account-name">{{ displayUserName }}</span>
              <span class="sidebar-account-role">{{ userRoleLabel }}</span>
            </div>
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              class="user-chevron sidebar-account-chevron"
              :class="{ 'sidebar-account-chevron-open': accountMenuOpen }"
            >
              <path d="m6 9 6 6 6-6"/>
            </svg>
          </button>
          <div v-if="accountMenuOpen" class="sidebar-account-popover" role="menu">
            <button class="sidebar-account-action" role="menuitem" @click="signOut">Sign out</button>
          </div>
        </div>
      </aside>

      <main ref="mainContentRef" class="main-content">
        <RouterView />
      </main>
    </div>

    <!-- Non-authenticated -->
    <main v-else class="main-shell">
      <RouterView />
    </main>

    <ToastContainer />
  </div>
</template>

<style>
@import url("https://fonts.googleapis.com/css2?family=Montserrat:wght@600;700;800&display=swap");
* { box-sizing: border-box; }
html, body, #app { height: 100%; margin: 0; }
:root {
  --brand-primary: #144734;
  --brand-primary-hover: #0f3a2a;
  --brand-primary-soft: #edf6f2;
  --brand-primary-soft-strong: #e3f1eb;
  --brand-primary-border: #cfe2d8;
  --brand-secondary: #D7AD51;
  --brand-secondary-soft: #fff7e6;
  --brand-secondary-border: #efd8a4;
  --text-strong: #1f2f28;
  --text-main: #31433b;
  --text-muted: #6d7f76;
  --surface-page: #fdfefe;
  --surface-card: #ffffff;
  --surface-muted: #fdfefe;
  --border-soft: #e2ece7;
  --border-strong: #d3e0d9;
  --shadow-soft: 0 8px 24px rgba(20, 71, 52, 0.06);
  --radius-md: 12px;
  --radius-lg: 16px;
  --title-font: "Montserrat", "Segoe UI", "Helvetica Neue", Arial, sans-serif;
}
body {
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto,
    "Helvetica Neue", Arial, sans-serif;
  background: var(--surface-page);
  color: var(--text-strong);
}

.main-content h1,
.main-content .page-header h2,
.main-content .header-title-row h2 {
  font-family: var(--title-font);
  letter-spacing: 0.01em;
}
button.link {
  background: none; border: none; color: inherit;
  cursor: pointer; text-decoration: underline;
  padding: 0; font: inherit;
}
/* ── Shell ── */
.app { display: flex; flex-direction: column; height: 100%; }
/* ── Body ── */
.app-body { display: flex; flex: 1; overflow: hidden; min-height: 0; }
/* ── Sidebar ── */
.sidebar {
  width: 220px; flex-shrink: 0;
  background: var(--surface-card); border-right: 1px solid var(--border-soft);
  display: flex; flex-direction: column; overflow: hidden;
}
.sidebar-brand {
  display: flex;
  align-items: center;
  gap: 0;
  padding: 24px 12px 10px;
  border-bottom: none;
  margin-bottom: 10px;
}
.brand-logo-link {
  display: inline-flex;
  border-radius: 10px;
  text-decoration: none;
}
.brand-logo-link:focus-visible {
  outline: 2px solid rgba(20, 71, 52, 0.35);
  outline-offset: 2px;
}
.brand-icon-wrap { width: 166px; height: 44px; display: flex; align-items: center; justify-content: flex-start; }
.brand-c-image { width: 100%; height: 100%; object-fit: contain; object-position: left center; display: block; }
.user-avatar {
  width: 32px; height: 32px; border-radius: 50%;
  background: linear-gradient(135deg, var(--brand-primary), #1f6a4d);
  color: #fff;
  display: flex; align-items: center; justify-content: center;
  font-weight: 600; font-size: 0.85rem; flex-shrink: 0;
}
.user-chevron { width: 14px; height: 14px; color: var(--text-muted); }
.sidebar-nav {
  flex: 1; padding: 8px 10px 10px;
  display: flex; flex-direction: column; gap: 6px;
  overflow-y: auto;
}
.nav-item {
  display: flex; align-items: center; gap: 10px;
  min-height: 44px;
  padding: 0 12px; border-radius: var(--radius-md);
  font-size: 0.88rem; color: var(--text-main);
  text-decoration: none; cursor: pointer;
  transition: background 0.12s, color 0.12s;
  user-select: none; white-space: nowrap; position: relative;
}
.nav-item:hover:not(.nav-disabled) { background: #f1f6f3; color: var(--text-strong); }
.nav-item.nav-active {
  background: var(--brand-primary-soft);
  color: var(--brand-primary);
  font-weight: 600;
  padding-left: 16px;
}
.nav-item.nav-active::before {
  content: "";
  position: absolute;
  left: 6px;
  top: 50%;
  transform: translateY(-50%);
  width: 3px;
  height: 20px;
  border-radius: 999px;
  background: var(--brand-primary);
}
.nav-item.nav-active .nav-icon svg { stroke: var(--brand-primary) !important; }
.nav-item.nav-disabled { color: #aab7b1; cursor: default; }
.nav-item.nav-disabled .nav-icon svg { stroke: #c0cad8 !important; }
.nav-icon { width: 18px; height: 18px; flex-shrink: 0; display: flex; align-items: center; }
.nav-icon svg { width: 18px; height: 18px; stroke: var(--text-muted); }
.soon-badge {
  margin-left: auto;
  font-size: 0.64rem; font-weight: 600;
  background: var(--brand-secondary-soft);
  color: #8a6a23;
  border: 1px solid var(--brand-secondary-border);
  padding: 1px 6px; border-radius: 999px;
}
.sidebar-account-menu {
  margin: 10px;
  position: relative;
}
.sidebar-account-summary {
  width: 100%;
  border: none;
  background: transparent;
  font: inherit;
  color: inherit;
  text-align: left;
  display: flex;
  align-items: center;
  gap: 10px;
  min-height: 44px;
  padding: 8px 10px;
  cursor: pointer;
  border-radius: var(--radius-md);
}
.sidebar-account-summary:hover { background: #f1f6f3; }
.sidebar-account-summary:focus-visible {
  outline: 2px solid rgba(20, 71, 52, 0.35);
  outline-offset: 2px;
}
.sidebar-account-meta {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 1px;
}
.sidebar-account-name {
  font-size: 0.84rem;
  color: var(--text-strong);
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.sidebar-account-role {
  font-size: 0.72rem;
  color: var(--text-muted);
}
.sidebar-account-chevron {
  transition: transform 0.15s;
}
.sidebar-account-chevron-open {
  transform: rotate(180deg);
}
.sidebar-account-popover {
  position: absolute;
  left: 0;
  right: 0;
  bottom: calc(100% + 8px);
  border: 1px solid var(--border-soft);
  border-radius: var(--radius-md);
  background: #fff;
  box-shadow: 0 12px 26px rgba(20, 71, 52, 0.14);
  padding: 6px;
  z-index: 40;
}
.sidebar-account-action {
  width: 100%;
  border: none;
  border-radius: 8px;
  background: transparent;
  color: var(--text-main);
  font-size: 0.8rem;
  text-align: left;
  padding: 9px 10px;
  cursor: pointer;
}
.sidebar-account-action:hover {
  background: var(--surface-muted);
}
/* ── Main content ── */
.main-content { flex: 1; overflow-y: auto; padding: 24px 28px 20px; background: var(--surface-page); }
.main-content > * { width: 100%; max-width: 1720px; margin: 0 auto; }
.main-shell {
  width: 100%;
  height: 100%;
  max-width: none;
  margin: 0;
  padding: 0;
  overflow: hidden;
}
</style>
